import type { FormEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { createDestination, DestinationPayload, DestinationUploadBundle, updateDestination } from '../api/Destinations';
import { generateWebsiteV2DestinationDraft } from '../api/DestinationsAI';
import {
  Destination,
  DestinationForm,
  DossierItem,
  MediaAsset,
  PrimeIndexCategory,
  RecommendedExperience,
  RouteHighlight
} from '../types/Destination';

const createMediaAsset = (): MediaAsset => ({ src: '', alt: '' });
const createPrimeCategory = (): PrimeIndexCategory => ({ label: '', score: 0 });
const createDossierItem = (): DossierItem => ({ label: '', value: '' });
const createRouteHighlight = (): RouteHighlight => ({ title: '', stopLabel: '', description: '' });
const createExperience = (): RecommendedExperience => ({ title: '', description: '', image: createMediaAsset() });

const createInitialForm = (): DestinationForm => ({
  slug: '',
  title: '',
  countryOrRegion: '',
  summary: '',
  tags: [],
  hero: {
    image: createMediaAsset(),
    eyebrow: '',
    headline: '',
    subheadline: ''
  },
  story: [''],
  bestFor: [],
  bestTimeToVisit: '',
  idealDuration: '',
  travelFeel: '',
  primeIndex: {
    score: 0,
    verdict: '',
    categories: [createPrimeCategory()],
    quote: ''
  },
  dossier: [createDossierItem()],
  editorNote: {
    body: '',
    authorName: '',
    authorRole: ''
  },
  routeHighlights: [createRouteHighlight()],
  recommendedExperiences: [createExperience()],
  gallery: [createMediaAsset()],
  recommendedJourneySlugs: [],
  seo: {
    title: '',
    description: '',
    canonicalPath: '',
    ogImage: createMediaAsset()
  },
  isPublished: false,
  publishedAt: null
});

const ensureArray = <T,>(value: T[] | undefined, create: () => T): T[] => {
  if (Array.isArray(value) && value.length > 0) return value;
  return [create()];
};

const toFormData = (destination: Destination): DestinationForm => ({
  ...destination,
  tags: Array.isArray(destination.tags) ? [...destination.tags] : [],
  story: ensureArray(destination.story, () => ''),
  bestFor: Array.isArray(destination.bestFor) ? [...destination.bestFor] : [],
  primeIndex: destination.primeIndex
    ? {
        ...destination.primeIndex,
        categories: ensureArray(destination.primeIndex.categories, createPrimeCategory)
      }
    : {
        score: 0,
        verdict: '',
        categories: [createPrimeCategory()],
        quote: ''
      },
  dossier: ensureArray(destination.dossier, createDossierItem),
  editorNote: destination.editorNote || { body: '', authorName: '', authorRole: '' },
  routeHighlights: ensureArray(destination.routeHighlights, createRouteHighlight),
  recommendedExperiences: ensureArray(destination.recommendedExperiences, createExperience).map((item) => ({
    ...item,
    image: item.image || createMediaAsset()
  })),
  gallery: ensureArray(destination.gallery, createMediaAsset),
  recommendedJourneySlugs: Array.isArray(destination.recommendedJourneySlugs) ? [...destination.recommendedJourneySlugs] : [],
  hero: {
    image: destination.hero?.image || createMediaAsset(),
    eyebrow: destination.hero?.eyebrow || '',
    headline: destination.hero?.headline || '',
    subheadline: destination.hero?.subheadline || ''
  },
  seo: {
    title: destination.seo?.title || '',
    description: destination.seo?.description || '',
    canonicalPath: destination.seo?.canonicalPath || '',
    ogImage: destination.seo?.ogImage || createMediaAsset()
  }
});

const trimMedia = (media?: MediaAsset): MediaAsset | undefined => {
  if (!media) return undefined;
  const src = (media.src || '').trim();
  const alt = (media.alt || '').trim();
  const width = media.width ? Number(media.width) : undefined;
  const height = media.height ? Number(media.height) : undefined;
  if (!src && !alt) return undefined;
  return {
    src,
    alt,
    ...(width ? { width } : {}),
    ...(height ? { height } : {})
  };
};

const cleanStringList = (values: string[] | undefined) =>
  (values || []).map((item) => item.trim()).filter(Boolean);

const mapDraftMedia = (media?: { url?: string; alt?: string } | null): MediaAsset | undefined => {
  if (!media) return undefined;
  return {
    src: media.url || '',
    alt: media.alt || ''
  };
};

type UseDestinationFormArgs = {
  onCreated: () => Promise<void> | void;
};

export const useDestinationForm = ({ onCreated }: UseDestinationFormArgs) => {
  const [formData, setFormData] = useState<DestinationForm>(createInitialForm());
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [aiFilling, setAiFilling] = useState(false);
  const [heroImageFile, setHeroImageFile] = useState<File | null>(null);
  const [seoOgImageFile, setSeoOgImageFile] = useState<File | null>(null);
  const [galleryImageFiles, setGalleryImageFiles] = useState<Array<File | null>>([]);
  const [recommendedExperienceImageFiles, setRecommendedExperienceImageFiles] = useState<Array<File | null>>([]);

  const isEditMode = Boolean(editingKey);

  const handleFormField = useCallback((field: keyof DestinationForm, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateCsvField = useCallback((field: 'tags' | 'bestFor' | 'recommendedJourneySlugs', value: string) => {
    const next = value.split(',').map((item) => item.trim()).filter(Boolean);
    setFormData((prev) => ({ ...prev, [field]: next }));
  }, []);

  const autofillFromAI = useCallback(async () => {
    const title = (formData.title || '').trim();
    if (!title) {
      toast.warning('Enter a website title before generating the AI draft.');
      return;
    }

    setAiFilling(true);
    try {
      const result = await generateWebsiteV2DestinationDraft({
        title,
        countryOrRegion: (formData.countryOrRegion || '').trim() || undefined,
        tags: cleanStringList(formData.tags)
      });
      const draft = result.draft || {};
      const nextSlug = (draft.slug || formData.slug || title)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      setFormData((prev) => ({
        ...prev,
        slug: nextSlug,
        title: draft.title || prev.title,
        countryOrRegion: draft.countryOrRegion || prev.countryOrRegion,
        summary: draft.summary || prev.summary,
        tags: Array.isArray(draft.tags) ? draft.tags : prev.tags,
        story: Array.isArray(draft.story) && draft.story.length ? draft.story : prev.story,
        bestFor: Array.isArray(draft.bestFor) ? draft.bestFor : prev.bestFor,
        bestTimeToVisit: draft.bestTimeToVisit || prev.bestTimeToVisit,
        idealDuration: draft.idealDuration || prev.idealDuration,
        travelFeel: draft.travelFeel || prev.travelFeel,
        hero: draft.hero
          ? {
              image: mapDraftMedia(draft.hero.image) || prev.hero.image,
              eyebrow: draft.hero.eyebrow || prev.hero.eyebrow,
              headline: draft.hero.headline || prev.hero.headline,
              subheadline: draft.hero.subheadline || prev.hero.subheadline
            }
          : prev.hero,
        primeIndex: draft.primeIndex
          ? {
              score: Number(draft.primeIndex.score ?? prev.primeIndex?.score ?? 0),
              verdict: draft.primeIndex.verdict || prev.primeIndex?.verdict || '',
              categories:
                draft.primeIndex.categories?.length
                  ? draft.primeIndex.categories.map((item) => ({
                      label: item.label || '',
                      score: Number(item.score ?? 0)
                    }))
                  : prev.primeIndex?.categories || [],
              quote: draft.primeIndex.quote || prev.primeIndex?.quote || ''
            }
          : prev.primeIndex,
        dossier:
          Array.isArray(draft.dossier) && draft.dossier.length
            ? draft.dossier.map((item) => ({
                label: item.label || '',
                value: item.value || ''
              }))
            : prev.dossier,
        editorNote: draft.editorNote
          ? {
              body: draft.editorNote.body || '',
              authorName: draft.editorNote.authorName || '',
              authorRole: draft.editorNote.authorRole || ''
            }
          : prev.editorNote,
        routeHighlights:
          Array.isArray(draft.routeHighlights) && draft.routeHighlights.length
            ? draft.routeHighlights.map((item) => ({
                title: item.title || '',
                stopLabel: item.stopLabel || '',
                description: item.description || ''
              }))
            : prev.routeHighlights,
        recommendedExperiences:
          Array.isArray(draft.recommendedExperiences) && draft.recommendedExperiences.length
            ? draft.recommendedExperiences.map((item) => ({
                title: item.title || '',
                description: item.description || '',
                image: mapDraftMedia(item.image) || createMediaAsset()
              }))
            : prev.recommendedExperiences,
        recommendedJourneySlugs: Array.isArray(draft.recommendedJourneySlugs)
          ? draft.recommendedJourneySlugs
          : prev.recommendedJourneySlugs,
        seo: {
          title: draft.seo?.title || prev.seo.title || draft.title || prev.title,
          description: draft.seo?.description || prev.seo.description || draft.summary || prev.summary,
          canonicalPath: prev.seo.canonicalPath || `/destinations/${nextSlug}`,
          ogImage: mapDraftMedia(draft.seo?.ogImage) || prev.seo.ogImage
        }
      }));
    } catch (e: any) {
      toast.error(`AI autofill failed: ${String(e?.message || e)}`);
    } finally {
      setAiFilling(false);
    }
  }, [formData.countryOrRegion, formData.slug, formData.tags, formData.title]);

  const resetDraft = useCallback((clearEditMode = true) => {
    setFormData(createInitialForm());
    setHeroImageFile(null);
    setSeoOgImageFile(null);
    setGalleryImageFiles([]);
    setRecommendedExperienceImageFiles([]);
    if (clearEditMode) setEditingKey(null);
  }, []);

  const resetForm = useCallback(() => {
    resetDraft(false);
  }, [resetDraft]);

  const startEditing = useCallback((destination: Destination) => {
    setEditingKey(destination.slug);
    setFormData(toFormData(destination));
    setHeroImageFile(null);
    setSeoOgImageFile(null);
    setGalleryImageFiles(new Array(destination.gallery?.length || 0).fill(null));
    setRecommendedExperienceImageFiles(new Array(destination.recommendedExperiences?.length || 0).fill(null));
  }, []);

  const cancelEditing = useCallback(() => {
    resetDraft(true);
  }, [resetDraft]);

  const handleHeroImageFileChange = useCallback((file: File | null) => {
    setHeroImageFile(file);
  }, []);

  const clearHeroImageFile = useCallback(() => {
    setHeroImageFile(null);
  }, []);

  const handleSeoOgImageFileChange = useCallback((file: File | null) => {
    setSeoOgImageFile(file);
  }, []);

  const clearSeoOgImageFile = useCallback(() => {
    setSeoOgImageFile(null);
  }, []);

  const handleGalleryImageFileChange = useCallback((idx: number, file: File | null) => {
    setGalleryImageFiles((prev) => {
      const next = [...prev];
      while (next.length <= idx) next.push(null);
      next[idx] = file;
      return next;
    });
  }, []);

  const clearGalleryImageFile = useCallback((idx: number) => {
    setGalleryImageFiles((prev) => {
      const next = [...prev];
      if (idx < next.length) next[idx] = null;
      return next;
    });
  }, []);

  const handleRecommendedExperienceImageFileChange = useCallback((idx: number, file: File | null) => {
    setRecommendedExperienceImageFiles((prev) => {
      const next = [...prev];
      while (next.length <= idx) next.push(null);
      next[idx] = file;
      return next;
    });
  }, []);

  const clearRecommendedExperienceImageFile = useCallback((idx: number) => {
    setRecommendedExperienceImageFiles((prev) => {
      const next = [...prev];
      if (idx < next.length) next[idx] = null;
      return next;
    });
  }, []);

  const canSubmit = useMemo(() => {
    if (!(formData.slug || '').trim()) return false;
    if (!(formData.title || '').trim()) return false;
    if (!(formData.summary || '').trim()) return false;
    if (!(formData.hero.image.src || '').trim() && !heroImageFile) return false;
    if (!(formData.hero.image.alt || '').trim()) return false;
    return true;
  }, [formData, heroImageFile]);

  const buildPayload = useCallback((): DestinationPayload => {
    const slug = formData.slug.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const title = formData.title.trim();
    const summary = formData.summary.trim();
    const gallery = (formData.gallery || []).map(trimMedia).filter(Boolean) as MediaAsset[];
    const recommendedExperiences = (formData.recommendedExperiences || [])
      .map((item) => {
        const titleValue = item.title.trim();
        const description = item.description.trim();
        const image = trimMedia(item.image);
        if (!titleValue || !description) return null;
        return {
          title: titleValue,
          description,
          ...(image ? { image } : {})
        };
      })
      .filter(Boolean) as DestinationPayload['recommendedExperiences'];
    const routeHighlights = (formData.routeHighlights || [])
      .map((item) => ({
        title: item.title.trim(),
        stopLabel: item.stopLabel.trim(),
        description: (item.description || '').trim()
      }))
      .filter((item) => item.title && item.stopLabel)
      .map((item) => ({
        title: item.title,
        stopLabel: item.stopLabel,
        ...(item.description ? { description: item.description } : {})
      }));
    const dossier = (formData.dossier || [])
      .map((item) => ({ label: item.label.trim(), value: item.value.trim() }))
      .filter((item) => item.label && item.value);
    const story = cleanStringList(formData.story);
    const seoOgImage = trimMedia(formData.seo.ogImage);
    const heroImage = trimMedia(formData.hero.image);

    if ((!heroImage?.src && !heroImageFile) || !heroImage?.alt) {
      throw new Error('Hero image and alt text are required.');
    }

    return {
      slug,
      title,
      countryOrRegion: formData.countryOrRegion.trim(),
      summary,
      tags: cleanStringList(formData.tags),
      hero: {
        image: heroImage || { src: '', alt: formData.hero.image.alt.trim() },
        eyebrow: (formData.hero.eyebrow || '').trim() || undefined,
        headline: (formData.hero.headline || '').trim() || title,
        subheadline: (formData.hero.subheadline || '').trim()
      },
      story,
      bestFor: cleanStringList(formData.bestFor),
      bestTimeToVisit: formData.bestTimeToVisit.trim(),
      idealDuration: formData.idealDuration.trim(),
      travelFeel: formData.travelFeel.trim(),
      primeIndex: formData.primeIndex
        ? {
            score: Number(formData.primeIndex.score) || 0,
            verdict: (formData.primeIndex.verdict || '').trim(),
            categories: (formData.primeIndex.categories || [])
              .map((item) => ({
                label: item.label.trim(),
                score: Number(item.score) || 0
              }))
              .filter((item) => item.label),
            quote: (formData.primeIndex.quote || '').trim() || undefined
          }
        : undefined,
      dossier,
      editorNote: formData.editorNote && formData.editorNote.body.trim()
        ? {
            body: formData.editorNote.body.trim(),
            authorName: formData.editorNote.authorName.trim(),
            authorRole: formData.editorNote.authorRole.trim()
          }
        : undefined,
      routeHighlights,
      recommendedExperiences,
      gallery,
      recommendedJourneySlugs: cleanStringList(formData.recommendedJourneySlugs),
      seo: {
        title: (formData.seo.title || '').trim() || title,
        description: (formData.seo.description || '').trim() || summary,
        canonicalPath: (formData.seo.canonicalPath || '').trim() || `/destinations/${slug}`,
        ...(seoOgImage ? { ogImage: seoOgImage } : {})
      },
      isPublished: Boolean(formData.isPublished),
      publishedAt: formData.isPublished ? formData.publishedAt || null : null
    };
  }, [formData, heroImageFile]);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    if (!canSubmit) {
      toast.warning('Fill the required Website V2 destination fields before saving.');
      return;
    }

    let payload: DestinationPayload;
    try {
      payload = buildPayload();
    } catch (error) {
      toast.warning((error as Error).message);
      return;
    }

    setSubmitting(true);
    try {
      const uploads: DestinationUploadBundle = {
        heroImageFile,
        seoOgImageFile,
        galleryImageFiles,
        recommendedExperienceImageFiles,
      };
      if (isEditMode && editingKey) {
        await updateDestination(editingKey, payload, uploads);
        toast.success('Destination updated successfully.');
        cancelEditing();
      } else {
        await createDestination(payload, uploads);
        toast.success('Destination created successfully.');
        resetForm();
      }
      await onCreated();
    } catch (error) {
      toast.error('Failed to save destination. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [buildPayload, canSubmit, cancelEditing, editingKey, galleryImageFiles, heroImageFile, isEditMode, onCreated, recommendedExperienceImageFiles, resetForm, seoOgImageFile]);

  return {
    formData,
    isEditMode,
    editingKey,
    canSubmit,
    submitting,
    aiFilling,
    heroImageFile,
    seoOgImageFile,
    galleryImageFiles,
    recommendedExperienceImageFiles,
    handleFormField,
    updateCsvField,
    autofillFromAI,
    handleHeroImageFileChange,
    clearHeroImageFile,
    handleSeoOgImageFileChange,
    clearSeoOgImageFile,
    handleGalleryImageFileChange,
    clearGalleryImageFile,
    handleRecommendedExperienceImageFileChange,
    clearRecommendedExperienceImageFile,
    startEditing,
    cancelEditing,
    resetForm,
    handleSubmit
  };
};
